import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { getStudentLessonWorkspace, getStudentMeCohorts, type StudentLessonWorkspace } from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import type { DemoTourStep } from "../student-ide/demoTourSteps";
import { StudentIdeBody, type LessonRuntimeMarkDoneSpec } from "../student-ide/StudentIdeBody";
import { StudentLessonAdvancedBody } from "../student-ide/StudentLessonAdvancedBody";
import { starterFilesToProjectFiles } from "../teacher/course-template-helpers";
import type { AssignmentProgressStatus } from "../student/assignmentProgressStatus";
import { StudentAssignmentGatePage } from "../student/StudentAssignmentGatePage";
import { buildLessonImmersiveTheme } from "../teacher/lessonImmersiveTheme";
import type { LessonWorkspaceMode } from "../auth/api-client";
import { ImmersiveSplash } from "../transition/ImmersiveSplash";
import { LAUNCH_STATE_FLAG } from "../transition/LessonLaunchProvider";

function isLessonNotAssignedError(err: unknown): boolean {
  return err instanceof Error && err.message === "Lesson is not assigned yet";
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const DEFAULT_MAIN_PY = `# Try the demo: click Run, answer the prompt, then edit me.
print("Hello from Code Create Slay!")

name = input("What's your name? ")
print(f"Nice to meet you, {name}!")
`;

const STORAGE_KEY_DEMO = "ccs-student-ide-main-py";

function mapWorkspaceTests(tests: StudentLessonWorkspace["lessonTests"]): LessonRuntimeMarkDoneSpec[] {
  return [...tests]
    .sort((a, b) => a.ordinal - b.ordinal || a.id.localeCompare(b.id))
    .map((t) => ({
      id: t.id,
      name: t.name,
      testCode: t.testCode ?? "",
      isRequired: t.isRequired,
      visibility: t.visibility,
      description: t.description ?? null,
    }));
}

function mapTourSteps(ws: StudentLessonWorkspace): readonly DemoTourStep[] {
  const mapped: DemoTourStep[] = ws.tourSteps.map((t) => ({
    title: t.title,
    body: t.body.length > 0 ? t.body : ["(No instructions for this step.)"],
    referenceCode: t.referenceCode ?? undefined,
  }));
  return mapped.length > 0
    ? mapped
    : [{ title: "Instructions", body: ["Follow your teacher’s directions for this lesson."] }];
}

/** Student lesson workspace: assignment mode when `cohortId` + `lessonId` query params are set; otherwise demo. */
export default function StudentIdePage() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const cohortId = sp.get("cohortId")?.trim() ?? "";
  const lessonId = sp.get("lessonId")?.trim() ?? "";
  const lessonTitleHint = sp.get("lessonTitle")?.trim();
  const iconName = sp.get("iconName")?.trim() || undefined;
  const iconBgColor = sp.get("iconBgColor")?.trim() || undefined;
  const iconFgColor = sp.get("iconFgColor")?.trim() || undefined;
  const wsModeParam = sp.get("workspaceMode")?.trim() || undefined;
  const reviewClosed = sp.get("review") === "1";

  const assignmentMode = Boolean(cohortId && lessonId && UUID_RE.test(cohortId) && UUID_RE.test(lessonId));
  const assignmentParamsInvalid = Boolean((cohortId || lessonId) && !assignmentMode);

  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();
  const location = useLocation();
  const introMode = (location.state as Record<string, unknown> | null)?.[LAUNCH_STATE_FLAG] ? "arrive" : "wash";

  const immersiveTheme = useMemo(
    () =>
      assignmentMode
        ? buildLessonImmersiveTheme({
            iconName,
            iconBgColor,
            iconFgColor,
            workspaceMode: wsModeParam as LessonWorkspaceMode | undefined,
            seed: lessonId,
          })
        : undefined,
    [assignmentMode, iconName, iconBgColor, iconFgColor, wsModeParam, lessonId]
  );

  const [loading, setLoading] = useState(assignmentMode);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [gateKind, setGateKind] = useState<"closed" | "not_open" | null>(null);
  const [workspace, setWorkspace] = useState<StudentLessonWorkspace | null>(null);
  const [demoBlocked, setDemoBlocked] = useState<boolean | null>(null);

  useEffect(() => {
    if (assignmentMode || !bearer) {
      setDemoBlocked(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const data = await getStudentMeCohorts(bearer);
        if (!cancelled && data.cohorts.length > 0) {
          setDemoBlocked(true);
          navigate("/lessons", { replace: true });
        } else if (!cancelled) {
          setDemoBlocked(false);
        }
      } catch {
        if (!cancelled) setDemoBlocked(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assignmentMode, bearer, navigate]);

  useEffect(() => {
    if (!assignmentMode) {
      setLoading(false);
      setWorkspace(null);
      setLoadErr(null);
      setGateKind(null);
      return;
    }
    let cancelled = false;
    (async () => {
      if (!bearer) {
        setLoadErr("Sign in to open this lesson.");
        setGateKind(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      setLoadErr(null);
      setGateKind(null);
      try {
        const ws = await getStudentLessonWorkspace(bearer, cohortId, lessonId);
        if (!cancelled) {
          if (ws.assignmentClosed && !reviewClosed) {
            setWorkspace(null);
            setGateKind("closed");
            setLoadErr(null);
          } else {
            setWorkspace(ws);
            setGateKind(null);
            setLoadErr(null);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setWorkspace(null);
          if (isLessonNotAssignedError(e)) {
            setGateKind("not_open");
            setLoadErr(null);
          } else {
            setGateKind(null);
            setLoadErr(e instanceof Error ? e.message : "Could not load lesson");
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assignmentMode, bearer, cohortId, lessonId, reviewClosed]);

  const storageKey = useMemo(
    () => (assignmentMode ? `ccs-student-asn-${cohortId}-${lessonId}` : STORAGE_KEY_DEMO),
    [assignmentMode, cohortId, lessonId]
  );

  if (assignmentParamsInvalid) {
    return (
      <main className="page">
        <div className="card card-wide">
          <div className="warn-banner" role="alert">
            Invalid lesson link. Open it again from your assigned lessons list.
          </div>
          <Link to="/lessons" className="btn secondary">
            Back to lessons
          </Link>
        </div>
      </main>
    );
  }

  if (assignmentMode && loading) {
    if (immersiveTheme) {
      return <ImmersiveSplash theme={immersiveTheme} label="Loading lesson…" />;
    }
    return (
      <main className="page">
        <div className="card">
          <p className="muted">Loading lesson…</p>
        </div>
      </main>
    );
  }

  if (assignmentMode && gateKind) {
    return <StudentAssignmentGatePage kind={gateKind} lessonTitle={lessonTitleHint} />;
  }

  if (assignmentMode && loadErr) {
    return (
      <main className="page">
        <div className="card card-wide">
          <div className="warn-banner" role="alert">
            {loadErr}
          </div>
          <Link to="/lessons" className="btn secondary">
            Back to lessons
          </Link>
        </div>
      </main>
    );
  }

  if (assignmentMode && workspace) {
    const py = workspace.preview.mainPy?.trim() ? workspace.preview.mainPy : "";
    const initialMainPy =
      py ||
      '# Your teacher has not set a starter file yet.\nprint("Hello!")\n';
    const tests = mapWorkspaceTests(workspace.lessonTests);
    const isReadOnly = Boolean(workspace.readOnly || workspace.assignmentClosed);
    const lessonTitle = workspace.lesson.title?.trim() || lessonTitleHint || "Lesson";
    const eyebrow = isReadOnly ? "Student · Closed (read only)" : "Student";
    const workspaceMode = workspace.lesson.workspaceMode ?? "simple";

    if (workspaceMode === "advanced") {
      const starterFiles = starterFilesToProjectFiles(workspace.starterFiles);
      return (
        <StudentLessonAdvancedBody
          key={storageKey}
          eyebrow={eyebrow}
          title={lessonTitle}
          backTo="/lessons"
          backLabel="Lessons"
          initialFiles={starterFiles}
          filesStorageKey={`${storageKey}-files`}
          tourSteps={mapTourSteps(workspace)}
          lessonTestsForMarkDone={tests}
          readOnly={isReadOnly}
          assignmentProgressStatus={(workspace.progressStatus ?? "in_progress") as AssignmentProgressStatus}
          markDoneSubmissionTarget={isReadOnly ? undefined : { cohortId, lessonId }}
          workspaceResetTarget={isReadOnly ? undefined : { cohortId, lessonId }}
          immersiveTheme={immersiveTheme}
          introMode={introMode}
        />
      );
    }

    return (
      <StudentIdeBody
        key={storageKey}
        eyebrow={eyebrow}
        title={lessonTitle}
        backTo="/lessons"
        backLabel="Lessons"
        initialMainPy={initialMainPy}
        storageKey={storageKey}
        tourSteps={mapTourSteps(workspace)}
        lessonTestsForMarkDone={tests}
        readOnly={isReadOnly}
        assignmentProgressStatus={(workspace.progressStatus ?? "in_progress") as AssignmentProgressStatus}
        editorBootstrap={workspace.editorBootstrap ?? (workspace.latestSubmission ? "submission" : "starter")}
        markDoneSubmissionTarget={isReadOnly ? undefined : { cohortId, lessonId }}
        workspaceResetTarget={isReadOnly ? undefined : { cohortId, lessonId }}
        immersiveTheme={immersiveTheme}
        introMode={introMode}
      />
    );
  }

  const demoTitle = lessonTitleHint || "Demo IDE";

  if (!assignmentMode && demoBlocked === null) {
    return (
      <main className="page">
        <div className="card">
          <p className="muted">Loading…</p>
        </div>
      </main>
    );
  }

  if (!assignmentMode && demoBlocked) {
    return null;
  }

  return (
    <StudentIdeBody
      eyebrow="Student · Demo"
      title={demoTitle}
      backTo="/lessons"
      backLabel="Lessons"
      initialMainPy={DEFAULT_MAIN_PY}
      storageKey={STORAGE_KEY_DEMO}
    />
  );
}
